/**
 * TopBarTasksButton — Üst panelə qoşulan tapşırıqlar düyməsi.
 * - 30s polling: /api/task-notifications/summary
 * - Badge: oxunmamış event sayı
 * - Dropdown: 4 stat + 10 ən son event
 * - "Tam Tapşırıqlar səhifəsi" linki ana panelin Tasks tab-ına aparır
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { ListChecks, Bell, AlertCircle, CheckCircle, MessageSquare, UserPlus, ShieldCheck, RefreshCw, ChevronRight, X } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 30000;

const TYPE_ICON = {
  assigned: UserPlus,
  mentioned: MessageSquare,
  status_changed: RefreshCw,
  comment_added: MessageSquare,
  approval_needed: ShieldCheck,
  approved: CheckCircle,
  rejected: AlertCircle,
  overdue: AlertCircle,
  due_soon: AlertCircle,
  auto_created: RefreshCw,
};

const TYPE_COLOR = {
  assigned: 'text-blue-600',
  mentioned: 'text-purple-600',
  status_changed: 'text-zinc-600',
  comment_added: 'text-purple-600',
  approval_needed: 'text-amber-600',
  approved: 'text-emerald-600',
  rejected: 'text-red-600',
  overdue: 'text-red-600',
  due_soon: 'text-amber-600',
  auto_created: 'text-zinc-500',
};

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s əvvəl`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}dəq əvvəl`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}s əvvəl`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}g əvvəl`;
  return new Date(iso).toLocaleDateString('az-AZ', { day: 'numeric', month: 'short' });
}

export default function TopBarTasksButton({ onOpenTasksPage }) {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState({
    unread: 0,
    assigned_to_me: 0,
    overdue: 0,
    awaiting_approval: 0,
    recent: [],
  });
  const [loading, setLoading] = useState(false);
  const popRef = useRef(null);
  const btnRef = useRef(null);

  const token = localStorage.getItem('token');

  const fetchSummary = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/task-notifications/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(res.data || {});
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSummary();
    const id = setInterval(fetchSummary, POLL_MS);
    return () => clearInterval(id);
  }, [fetchSummary]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popRef.current && popRef.current.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/task-notifications/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchSummary();
    } catch (e) {
      // silent
    }
  };

  const handleOpenFullPage = () => {
    setOpen(false);
    onOpenTasksPage?.();
  };

  const badgeCount = summary.unread || 0;

  return (
    <div className="relative" data-testid="topbar-tasks-button">
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        className="relative inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-purple-100 transition"
        title="Tapşırıqlar"
        data-testid="topbar-tasks-trigger"
      >
        <ListChecks className="h-5 w-5 text-purple-700" />
        {badgeCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="topbar-tasks-badge"
          >
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={popRef}
          className="absolute right-0 mt-2 w-[360px] bg-white rounded-lg shadow-2xl border border-zinc-200 z-50 overflow-hidden"
          data-testid="topbar-tasks-dropdown"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b bg-gradient-to-r from-purple-50 to-pink-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-purple-700" />
              <span className="font-semibold text-sm">Tapşırıqlar</span>
            </div>
            <div className="flex items-center gap-1">
              {badgeCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[11px] text-purple-700 hover:underline"
                  data-testid="topbar-tasks-mark-all-read"
                >
                  Hamısını oxu
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-0.5 hover:bg-white rounded"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 divide-x border-b bg-white">
            <button
              onClick={handleOpenFullPage}
              className="px-2 py-2.5 hover:bg-zinc-50 text-center"
              data-testid="topbar-stat-assigned"
            >
              <div className="text-lg font-bold text-blue-700">{summary.assigned_to_me || 0}</div>
              <div className="text-[10px] text-zinc-600 leading-tight">Mənə təyin</div>
            </button>
            <button
              onClick={handleOpenFullPage}
              className="px-2 py-2.5 hover:bg-zinc-50 text-center"
              data-testid="topbar-stat-overdue"
            >
              <div className="text-lg font-bold text-red-600">{summary.overdue || 0}</div>
              <div className="text-[10px] text-zinc-600 leading-tight">Vaxtı keçib</div>
            </button>
            <button
              onClick={handleOpenFullPage}
              className="px-2 py-2.5 hover:bg-zinc-50 text-center"
              data-testid="topbar-stat-approval"
            >
              <div className="text-lg font-bold text-amber-600">{summary.awaiting_approval || 0}</div>
              <div className="text-[10px] text-zinc-600 leading-tight">Təsdiq gözl.</div>
            </button>
          </div>

          {/* Recent events */}
          <div className="max-h-[300px] overflow-y-auto">
            {(summary.recent || []).length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-zinc-400">
                <Bell className="h-6 w-6 mx-auto mb-2 opacity-40" />
                Yeni bildiriş yoxdur
              </div>
            ) : (
              (summary.recent || []).map((n) => {
                const Icon = TYPE_ICON[n.type] || Bell;
                const color = TYPE_COLOR[n.type] || 'text-zinc-600';
                return (
                  <div
                    key={n.id}
                    className={`px-3 py-2.5 border-b last:border-b-0 hover:bg-zinc-50 flex gap-2 ${
                      n.read ? '' : 'bg-purple-50/40'
                    }`}
                    data-testid={`topbar-event-${n.id}`}
                  >
                    <Icon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${color}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-zinc-800 leading-snug">{n.message}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">{timeAgo(n.created_at)}</div>
                    </div>
                    {!n.read && <div className="h-1.5 w-1.5 rounded-full bg-purple-500 mt-2 flex-shrink-0" />}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer link */}
          {onOpenTasksPage && (
            <button
              onClick={handleOpenFullPage}
              className="w-full px-4 py-2.5 text-xs text-purple-700 hover:bg-purple-50 font-medium flex items-center justify-center gap-1 border-t"
              data-testid="topbar-tasks-open-full"
            >
              Tam Tapşırıqlar səhifəsi <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
